import { isDefined } from "@clipboard-health/util-ts";
import { useMemo } from "react";

import { useAutoRefreshOffer } from "../../Open/useAutoRefreshOffer";

interface CompanionOffer {
  offerId: string;
  hourlyPayInMinorUnits: number;
}

/**
 * Creates/refreshes offers for companion shifts in a double.
 * Supports up to 2 companions (the maximum for a double: one before, one after the anchor).
 */
export function useCompanionOffers(companionShiftIds: string[]) {
  const firstId = companionShiftIds[0] as string | undefined;
  const secondId = companionShiftIds[1] as string | undefined;

  const first = useAutoRefreshOffer({ shiftId: firstId }, { enabled: isDefined(firstId) });
  const second = useAutoRefreshOffer({ shiftId: secondId }, { enabled: isDefined(secondId) });

  const companionOffers = useMemo(() => {
    const map = new Map<string, CompanionOffer>();
    if (isDefined(firstId) && isDefined(first.data.offer)) {
      map.set(firstId, {
        offerId: first.data.offer.id,
        hourlyPayInMinorUnits: first.data.offer.attributes.pay.amountInMinorUnits,
      });
    }

    if (isDefined(secondId) && isDefined(second.data.offer)) {
      map.set(secondId, {
        offerId: second.data.offer.id,
        hourlyPayInMinorUnits: second.data.offer.attributes.pay.amountInMinorUnits,
      });
    }

    return map;
  }, [firstId, secondId, first.data.offer, second.data.offer]);

  const isLoading =
    (isDefined(firstId) && first.isLoading) || (isDefined(secondId) && second.isLoading);

  return { companionOffers, isLoading };
}
